/**
 * Form Configuration Loader
 * 
 * Loads and parses YAML form configuration files for dynamic form generation.
 * The YAML files define form metadata, groups, and controls (UI only).
 * 
 * ## DRY Principle: Single Source of Truth
 * 
 * This loader enforces the DRY (Don't Repeat Yourself) principle by:
 * 
 * 1. **YAML is the single source of truth for UI metadata**
 *    - Field labels are defined ONCE in YAML
 *    - Placeholders defined ONCE in YAML
 *    - Control types defined ONCE in YAML
 *    - Select options defined ONCE in YAML
 * 
 * 2. **Composers load from YAML, never hardcode**
 *    ```typescript
 *    const controlsByGroup = FormConfigLoader.getControlsByGroup(FORM_CONFIG)
 *    // Use control.label from YAML directly
 *    const config: ControlConfig = {
 *      label: control.label,  // From YAML - single source!
 *    }
 *    ```
 * 
 * 3. **Render functions are generic**
 *    - Accept labels via ControlConfig parameter
 *    - Never contain hardcoded labels
 *    - Reusable across all forms
 * 
 * ## Architecture Layers
 * 
 * - **YAML (form.yml)**: UI metadata only - labels, placeholders, control types
 * - **Schema (schema.ts)**: Validation rules only - required, minLength, patterns
 * - **Composer (composer.tsx)**: Orchestration - loads YAML, fetches API options
 * - **Render Functions**: Generic rendering - uses labels from ControlConfig
 * 
 * ## Validation: Where It Belongs
 * 
 * ❌ DON'T put validation in YAML:
 * ```yaml
 * validations:
 *   required: true
 *   email: true
 * ```
 * 
 * ✅ DO put validation in Zod schema:
 * ```typescript
 * export const MySchema = z.object({
 *   email: z.string().email('Invalid email'),
 * })
 * ```
 * 
 * ## Usage Example
 * 
 * In your schema.ts file:
 * ```typescript
 * import formYaml from './form.yml?raw'
 * const formConfig = FormConfigLoader.loadFormConfig(formYaml)
 * 
 * // FORM_CONFIG becomes single source of truth for all metadata
 * export const FORM_CONFIG = formConfig
 * export const FORM_TITLE = formConfig.formTitle
 * export const FORM_DESCRIPTION = formConfig.formDescription
 * ```
 * 
 * In your composer.tsx file:
 * ```typescript
 * import { FORM_CONFIG } from './schema'
 * 
 * const controlsByGroup = FormConfigLoader.getControlsByGroup(FORM_CONFIG)
 * 
 * // When rendering, use labels from YAML:
 * const config: ControlConfig = {
 *   label: control.label,  // ← From YAML
 *   placeholder: control.placeholder,  // ← From YAML
 * }
 * return renderTextControl(config, bound)
 * ```
 * 
 * Based on ConEx form template structure.
 * See: src/forms/FORM_CONFIGURATION_GUIDE.md for complete patterns
 * 
 * NOTE: For browser compatibility, YAML files must be imported directly
 * in schema files, not loaded dynamically at runtime.
 */

// @ts-ignore
import yaml from 'js-yaml';
import type { UploaderConfig } from '@/lib/mediaUploadTypes';

// ============================================================================
// Type Definitions (matching ConEx template structure)
// ============================================================================

export interface FormControl {
  name: string;
  label: string;
  // Some form YAMLs extend the base template with additional control types.
  // Keep this permissive so the UI can evolve without fighting TS.
  htmlControl: 'input' | 'textarea' | 'select' | 'radio' | 'checkbox' | 'button' | 'file' | string;
  inputType?: 'text' | 'number' | 'email' | 'password' | 'date' | 'tel' | 'file';
  placeholder?: string;
  valueType?: 'integer' | 'number' | 'string' | 'date' | 'boolean' | 'array';
  selectOptions?: Record<string, string> | Array<{ value: string; label: string }>;
  selectMultiple?: boolean;
  controlType?: string;
  initialValue?: any;
  disabled?: boolean;
  readonly?: boolean;
  description?: string;
  visibility?: boolean;
  buttonClickAction?: string;
  // Additional fields for API integration
  optionsEndpoint?: string;
  dependsOn?: string;
  // Media uploader configuration for file controls
  uploader?: UploaderConfig;
}

export interface ControlConfig {
    name: string
    label: string
    placeholder?: string
    htmlControl: string
    options?: { label: string; value: string }[]
    required?: boolean
    readonly?: boolean
    description?: string
}

export interface FormGroupValidator {
  name: string;
  validationFn: string;
  controls: Array<{
    name: string;
    displayMessage?: boolean;
  }>;
}

export interface FormGroup {
  formGroupName: string;
  formGroupLabel?: string;
  formGroupValidators?: FormGroupValidator[];
  formControls: FormControl[];
}

export interface FormConfig {
  conexResourceId: string;
  formName: string;
  formTitle: string;
  formDescription: string;
  formGroup: FormGroup[];
  // Submit button configuration (not in template but useful)
  submitButton?: {
    label: string;
    loadingLabel?: string;
  };
  cancelButton?: {
    label: string;
  };
}

// ============================================================================
// Form Config Loader Class
// ============================================================================

export class FormConfigLoader {
  /**
   * Parse YAML string into FormConfig
   * @param yamlString - YAML content as string
   * @returns Parsed form configuration
   */
  static parseYamlString(yamlString: string): FormConfig {
    try {
      const config = yaml.load(yamlString) as FormConfig;
      
      // Validate required fields
      if (!config.formName || !config.formTitle || !config.formGroup) {
        throw new Error('Invalid form configuration: missing required fields (formName, formTitle, or formGroup)');
      }
      
      return config;
    } catch (error: any) {
      throw new Error(`Failed to parse YAML: ${error.message}`);
    }
  }

  /**
   * Load form configuration from imported YAML content
   * 
   * Usage in schema.ts:
   * ```typescript
   * import formYaml from './form.yml?raw'
   * const formConfig = FormConfigLoader.loadFormConfig(formYaml)
   * ```
   * 
   * @param yamlContent - YAML content (imported with ?raw)
   * @returns Parsed form configuration
   */
  static loadFormConfig(yamlContent: string): FormConfig {
    return this.parseYamlString(yamlContent);
  }

  /**
   * Get all form controls from all groups
   * @param config - Form configuration
   * @returns Array of all form controls
   */
  static getAllControls(config: FormConfig): FormControl[] {
    return config.formGroup.flatMap(group => group.formControls);
  }

  /**
   * Get controls grouped by form group
   * @param config - Form configuration
   * @returns Map of group name to controls
   */
  static getControlsByGroup(config: FormConfig): Map<string, FormControl[]> {
    const grouped = new Map<string, FormControl[]>();
    
    for (const group of config.formGroup) {
      grouped.set(group.formGroupName, group.formControls);
    }
    
    return grouped;
  }

  /**
   * Get a specific control by name
   * @param config - Form configuration
   * @param controlName - Name of the control to find
   * @returns Control configuration or undefined
   */
  static getControl(config: FormConfig, controlName: string): FormControl | undefined {
    return this.getAllControls(config).find(c => c.name === controlName);
  }

  /**
   * Get controls that depend on another control
   * @param config - Form configuration
   * @param controlName - Name of the control to check dependencies for
   * @returns Array of dependent controls
   */
  static getDependentControls(config: FormConfig, controlName: string): FormControl[] {
    return this.getAllControls(config).filter(c => c.dependsOn === controlName);
  }

  /**
   * Get form groups
   * @param config - Form configuration
   * @returns Array of form groups
   */
  static getGroups(config: FormConfig): FormGroup[] {
    return config.formGroup || [];
  }

  /**
   * Get a specific form group by name
   * @param config - Form configuration
   * @param groupName - Name of the group to find
   * @returns Form group or undefined
   */
  static getGroup(config: FormConfig, groupName: string): FormGroup | undefined {
    return config.formGroup.find(g => g.formGroupName === groupName);
  }

  /**
   * Convert selectOptions to array format
   * @param options - Select options (object or array)
   * @returns Array of option objects
   */
  static normalizeSelectOptions(options?: Record<string, string> | Array<{ value: string; label: string }>): Array<{ value: string; label: string }> {
    if (!options) return [];

    if (Array.isArray(options)) {
      return options;
    }

    // Convert object to array
    return Object.entries(options).map(([value, label]) => ({ value, label }));
  }

  /**
   * Get uploader configuration for a specific file control
   * @param config - Form configuration
   * @param controlName - Name of the file control
   * @returns Uploader configuration or undefined if not found
   */
  static getUploaderConfig(config: FormConfig, controlName: string): UploaderConfig | undefined {
    const control = this.getControl(config, controlName);
    return control?.uploader;
  }
}
